import { useState } from "react";
import {
  Button,
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  makeStyles,
  Theme,
} from "@material-ui/core";
import { Field, Form, Formik } from "formik";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import * as yup from "yup";
import { auth2FaService, usersService } from "../../../../api";
import { useDynamicYupPasswordValidations } from "../../../../libs/yup-validations";
import CustomPasswordField from "../../../forms/CustomPasswordField";
import { UserData } from "../../../../models/userData";
import TwoFaCode from "./TwoFaCode";
import { AppDispatch } from "../../../../redux/auth2fa/resend-code/types";
import { resend2FaCode } from "../../../../redux/auth2fa/resend-code/actions";
import { useDispatch } from "react-redux";

interface Update2FaForm {
  password: string;
  is_2fa_enabled: boolean;
}

interface Update2FaProps {
  user: UserData;
}

const initialValues = {
  password: "",
  is_2fa_enabled: false,
} as Update2FaForm;

/**
 * @component Update2Fa
 *
 * UI component for enabling or disabling Two-Factor Authentication (2FA).
 *
 * @param {Update2FaProps} props - Contains the current `user` object
 * @returns {JSX.Element} The rendered Update2Fa form and 2FA code input
 */
const Update2Fa = ({ user }: Update2FaProps) => {
  const { t } = useTranslation(["authentication", "snackbars"]);
  const dispatch = useDispatch();
  const classes = useStyles();

  const [is2FAEnabled, setIs2FAEnabled] = useState<boolean>(
    user?.is_2fa_enabled ?? false
  );
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [toggled, setToggled] = useState(false);
  const [password, setPassword] = useState<string>("");
  const [formKey, setFormKey] = useState(`2fa-${i18n.language}-${Date.now()}`);

  const { YupValidationPasswordRequired } = useDynamicYupPasswordValidations();

  const validationSchema = yup.object({
    password: YupValidationPasswordRequired,
  });

  /**
   * Resets the 2FA form to its initial state.
   *
   * This includes:
   * - Hiding the verification code input
   * - Resetting toggle state and password
   * - Restoring the original 2FA enabled state from the user prop
   * - Removing any stored session user ID
   */
  const clearForm = (isEnabled: boolean = user?.is_2fa_enabled) => {
    setShowCodeInput(false);
    setToggled(false);
    setPassword("");
    setIs2FAEnabled(isEnabled);
    sessionStorage.removeItem("pending_2fa_user_id");
    setFormKey(`2fa-${i18n.language}-${Date.now()}`);
  };

  /**
   * @function guardUserSession
   * Checks if the pending 2FA user ID exists in session storage.
   * If not, resets state and shows an error notification.
   * @returns userId if found, otherwise undefined.
   */
  const guardUserSession = () => {
    const userId = sessionStorage.getItem("pending_2fa_user_id");
    if (!userId) {
      enqueueSnackbar(t("auth2fa.sessionExpired", { ns: "snackbars" }), {
        variant: "error",
      });
      clearForm();
      return;
    }
    return userId;
  };

  /**
   * @function handleResend
   * Resends the 2FA code via Redux action.
   * Handles expired sessions and clears state if unauthorized.
   */
  const handleResend = () => {
    const userId = guardUserSession();
    if (userId) {
      (dispatch as AppDispatch)(resend2FaCode(userId))
        .then(() => {
          enqueueSnackbar(t("auth2fa.codeResent", { ns: "snackbars" }), {
            variant: "success",
          });
        })
        .catch((error: { response: { status: number } }) => {
          if (error?.response?.status === 401) {
            clearForm();
          }
        });
    }
  };

  /**
   * @function onSubmitCheckPasswordHandler
   * Validates the entered password before starting the 2FA process.
   * If valid, sends the 2FA code and shows the code input UI.
   */
  const onSubmitCheckPasswordHandler = async (values: Update2FaForm) => {
    const isPasswordValid = await auth2FaService.checkPassword(values.password);
    if (isPasswordValid?.status === 200) {
      setPassword(values.password);
      const isSendCode = await auth2FaService.send2FaCode();
      if (isSendCode.status === 200) {
        sessionStorage.setItem("pending_2fa_user_id", isSendCode.user_id);
        setShowCodeInput(true);
      }
    }
  };

  /**
   * @function handleVerifyCode
   * Verifies the entered 2FA code.
   * If valid, updates the 2FA setting on the server and resets all local state.
   */
  const handleVerifyCode = async (code: string) => {
    const userId = guardUserSession();
    if (userId) {
      const isVerified = await auth2FaService.verify2FaCode(userId, code);
      if (isVerified.status === 200) {
        const isUpdated = await usersService.update2FaSettings(password, is2FAEnabled);
        if (isUpdated.status === 200) {
          enqueueSnackbar(isUpdated.data.detail, {
            variant: "success",
          });
        }
        clearForm(isUpdated.data.is_2fa_enabled);
      }
    }
  };

  return (
    <>
      <Formik
        key={formKey}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmitCheckPasswordHandler}
      >
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">
                {t("auth2fa.sectionTitle", { ns: "authentication" })}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={8} md={7}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={is2FAEnabled}
                    onChange={(e) => {
                      setIs2FAEnabled(e.target.checked);
                      setToggled(true);
                    }}
                  />
                }
                label={t("auth2fa.enableToggle", { ns: "authentication" })}
              />
            </Grid>
            {!showCodeInput && toggled ? (
              <>
                <Grid item xs={12} sm={8} md={7}>
                  <Field
                    as={CustomPasswordField}
                    fullWidth
                    autoComplete="current-password"
                    id="password"
                    label={t("password.change.passwordCurrent", {
                      ns: "authentication",
                    })}
                    name="password"
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={8} md={8}>
                  <Button
                    type="submit"
                    size="large"
                    disabled={!toggled}
                    variant="contained"
                    color="primary"
                  >
                    {t("auth2fa.saveButton", { ns: "authentication" })}
                  </Button>
                </Grid>
              </>
            ) : null}
          </Grid>
        </Form>
      </Formik>

      {showCodeInput ? (
        <>
          <Typography className={classes.text}>
            {t("auth2fa.description", {
              ns: "authentication",
            })}
          </Typography>
          <TwoFaCode
            onSubmit={handleVerifyCode}
            onCancel={clearForm}
            onResend={handleResend}
          />
        </>
      ) : null}
    </>
  );
};

export default Update2Fa;

const useStyles = makeStyles((theme: Theme) => ({
  text: {
    marginTop: theme.spacing(1),
  },
}));
